// Reference rate-tier tables used by the customer accumulated-amount checker.
//
// Smile: accumulated amount (yen) -> product rate. A LOWER rate is better for
// the customer, so a customer whose accumulated amount qualifies for a tier
// should have a productRate <= that tier's rate.
//
// Tama has two customer types, told apart by the current transaction fee
// (ค่ากด). Each type improves a different benefit as the accumulated amount
// grows:
//   - Existing customers (ลูกเก่า): fixed fee, improving RATE discount
//     (extraPercentDiscount, a fraction matching the sheet's "bonus" column).
//   - New customers (ลูกใหม่): fixed rate, decreasing transaction fee (baht).
// Source: https://docs.google.com/spreadsheets/d/18YTTcB8wv_5hYo_LPEY5sTXTwWULOUSxSL373GB0RIg

// Smile tiers (ascending threshold). `rate` is the product rate at that tier.
export const SMILE_TIERS = [
  { threshold: 150000, rate: 0.255 },
  { threshold: 500000, rate: 0.25 },
  { threshold: 1000000, rate: 0.245 },
  { threshold: 1500000, rate: 0.24 },
];

// Tama rate-discount tiers (existing customers). `discount` is a fraction and
// is subtracted inside the rate formula (same units as profile.extraPercentDiscount).
export const TAMA_RATE_TIERS = [
  { threshold: 0, discount: 0 },
  { threshold: 750000, discount: 0.01 },
  { threshold: 1000000, discount: 0.02 },
  { threshold: 3000000, discount: 0.03 },
  { threshold: 5000000, discount: 0.04 },
  { threshold: 7500000, discount: 0.05 },
  { threshold: 10000000, discount: 0.055 },
];

// Tama transaction-fee tiers (new customers), in baht. A LOWER fee is better.
export const TAMA_FEE_TIERS = [
  { threshold: 0, fee: 70 },
  { threshold: 500000, fee: 50 },
  { threshold: 1500000, fee: 40 },
  { threshold: 5000000, fee: 20 },
  { threshold: 10000000, fee: 0 },
];

// Small tolerance so float rounding on stored rates does not create false
// "not adjusted" warnings.
const EPS = 1e-6;

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const num = parseFloat(String(value).replace(/,/g, ''));
  return Number.isNaN(num) ? null : num;
};

// Highest tier whose threshold the accumulated amount has reached.
const qualifiedTier = (tiers, amount) => {
  let result = null;
  for (const tier of tiers) {
    if (amount + EPS >= tier.threshold) result = tier;
  }
  return result;
};

// Lowest tier whose threshold is still above the accumulated amount.
const nextTier = (tiers, amount) =>
  tiers.find((tier) => tier.threshold > amount + EPS) || null;

// Customer's actual rate, mirroring calculateFlowRate in OrderForm.js:
//   standardRate * (todayRate / standardRate - discount) - extraFlowDiscount
export const computeTamaRate = (standardRate, todayRate, discount, extraFlowDiscount = 0) => {
  const sr = toNumber(standardRate);
  const tr = toNumber(todayRate);
  if (!sr || !tr) return null;
  const d = toNumber(discount) || 0;
  const flow = toNumber(extraFlowDiscount) || 0;
  const rate = sr * (tr / sr - d) - flow;
  return Math.round(rate * 10000) / 10000;
};

// The new-customer policy (starting transaction fee 70) began on 10 Jun 2026:
// anyone who registered on/after this date is a new customer, everyone before
// is an existing customer. (Month is 0-indexed, so 5 = June.)
export const TAMA_NEW_CUSTOMER_CUTOFF = new Date(2026, 5, 10);

// Infer 'old' or 'new'. The registration date is the defining signal. When it
// is missing, fall back to the profile: a rate discount marks an existing
// customer, a fixed 40 fee (or unknown) defaults to 'old', and any other fee
// (70/50/20/0) is a new customer. The admin can override with the toggle.
export const inferTamaType = (profile) => {
  const reg = profile?.registerDate ? new Date(profile.registerDate) : null;
  if (reg && !Number.isNaN(reg.getTime())) {
    return reg >= TAMA_NEW_CUSTOMER_CUTOFF ? 'new' : 'old';
  }
  if ((toNumber(profile?.extraPercentDiscount) || 0) > 0) return 'old';
  const fee = toNumber(profile?.transactionFee);
  if (fee === 40 || fee == null) return 'old';
  return 'new';
};

// --- Smile analysis -------------------------------------------------------
const analyseSmile = (amount, profile) => {
  const qualified = qualifiedTier(SMILE_TIERS, amount);
  const next = nextTier(SMILE_TIERS, amount);
  const currentRate = toNumber(profile?.productRate);
  const qualifiedRate = qualified ? qualified.rate : null;
  const needsAdjustment =
    qualified && currentRate != null && currentRate - qualifiedRate > EPS;
  return {
    mode: 'smile',
    amount,
    qualified,
    next,
    remainingToNext: next ? next.threshold - amount : null,
    currentRate,
    qualifiedRate,
    needsAdjustment,
  };
};

// --- Tama existing-customer analysis (rate discount) ----------------------
const analyseTamaOld = (amount, profile, rates) => {
  const qualified = qualifiedTier(TAMA_RATE_TIERS, amount);
  const next = nextTier(TAMA_RATE_TIERS, amount);
  const currentDiscount = toNumber(profile?.extraPercentDiscount) || 0;
  const qualifiedDiscount = qualified ? qualified.discount : 0;
  const needsAdjustment = qualified && qualifiedDiscount - currentDiscount > EPS;
  return {
    mode: 'tama-old',
    amount,
    qualified,
    next,
    remainingToNext: next ? next.threshold - amount : null,
    currentDiscount,
    qualifiedDiscount,
    currentFee: toNumber(profile?.transactionFee),
    currentRate: computeTamaRate(
      rates?.standardRate,
      rates?.todayRate,
      currentDiscount,
      profile?.extraFlowDiscount
    ),
    needsAdjustment,
  };
};

// --- Tama new-customer analysis (transaction fee) -------------------------
const analyseTamaNew = (amount, profile, rates) => {
  const qualified = qualifiedTier(TAMA_FEE_TIERS, amount);
  const next = nextTier(TAMA_FEE_TIERS, amount);
  const currentFee = toNumber(profile?.transactionFee);
  const qualifiedFee = qualified ? qualified.fee : null;
  // Lower fee is better: a warning fires when they qualify for a fee lower
  // than the one they currently have.
  const needsAdjustment =
    qualified && currentFee != null && qualifiedFee != null && currentFee - qualifiedFee > EPS;
  return {
    mode: 'tama-new',
    amount,
    qualified,
    next,
    remainingToNext: next ? next.threshold - amount : null,
    currentFee,
    qualifiedFee,
    currentRate: computeTamaRate(rates?.standardRate, rates?.todayRate, 0, profile?.extraFlowDiscount),
    needsAdjustment,
  };
};

/**
 * Analyse a customer against the reference tables for their shop.
 *
 * @param {'smile'|'tama'} shop
 * @param {'old'|'new'} tamaType - Tama customer type (ignored for smile)
 * @param {number|string} accumulatedAmount - lifetime accumulated amount (yen)
 * @param {object} profile - customer profile
 * @param {{standardRate:number, todayRate:number}} rates - today's Tama rates
 */
export const analyseCustomer = (shop, tamaType, accumulatedAmount, profile, rates = {}) => {
  const amount = toNumber(accumulatedAmount) || 0;
  if (shop === 'smile') return analyseSmile(amount, profile);
  if (tamaType === 'new') return analyseTamaNew(amount, profile, rates);
  return analyseTamaOld(amount, profile, rates);
};
